/**
 * ApplicabilityConstraintForm — typed editor for ApplicabilityConstraint ADT.
 *
 * Patterned after ConstraintConfigForm. Backed by Zod-validated discriminated
 * union (ApplicabilityConstraintSchema, depth ≤ 10) on the backend.
 *
 * Templates exposed in the typed form (subset of full ADT for AC-13):
 *   - alwaysTrue
 *   - dateBefore | dateOnOrAfter
 *   - equals
 *   - or | and (composite — children edited recursively)
 *
 * Advanced JSON toggle accepts the full ADT (including `not`, `in`,
 * `greaterThan`, `between`, etc.).
 *
 * @see AC-11 / AC-13 in docs/specifications/2026-05-05-billing-pricing-admin-ui/spec.md
 * @see apps/backend/src/billing/domain/applicability-matcher.ts
 */
import React, { useEffect, useState } from "react";

type Constraint = Record<string, unknown>;

const TEMPLATE_TYPES = [
  "alwaysTrue",
  "equals",
  "dateBefore",
  "dateOnOrAfter",
  "or",
  "and",
] as const;

type TemplateType = (typeof TEMPLATE_TYPES)[number];

const FIELD_OPTIONS = [
  // Pricing context
  "billingInterval",
  "orderMonth",
  "tenantId",
  "promoCode",
  "productId",
  "brandId",
  "tenantCreatedAt",
  "orderDate",
  // Product applicability
  "channel",
  "hasSubscriptionHistory",
  "hasModuleOverlap",
  "nipIsUnique",
  "partyHasNoActiveTrial",
];

export interface ApplicabilityConstraintFormProps {
  value: Constraint;
  onChange: (next: Constraint) => void;
  /** Backend-side error to surface inline. */
  error?: string | null;
  /** Disable inputs (e.g. while saving). */
  disabled?: boolean;
}

/**
 * Convert ISO 8601 UTC string → `YYYY-MM-DDTHH:mm` for `<input type="datetime-local">`.
 * Returns empty string if input is not a parsable ISO date — falls back gracefully.
 */
function toLocalDateTime(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  // Strip seconds + timezone suffix; datetime-local expects local-naive YYYY-MM-DDTHH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function defaultsFor(type: TemplateType): Constraint {
  switch (type) {
    case "alwaysTrue":
      return { type: "alwaysTrue" };
    case "equals":
      return { type: "equals", field: "channel", value: "" };
    case "dateBefore":
      return {
        type: "dateBefore",
        field: "orderDate",
        value: new Date().toISOString(),
      };
    case "dateOnOrAfter":
      return {
        type: "dateOnOrAfter",
        field: "orderDate",
        value: new Date().toISOString(),
      };
    case "or":
      return {
        type: "or",
        constraints: [{ type: "alwaysTrue" }, { type: "alwaysTrue" }],
      };
    case "and":
      return {
        type: "and",
        constraints: [{ type: "alwaysTrue" }, { type: "alwaysTrue" }],
      };
  }
}

export const ApplicabilityConstraintForm: React.FC<
  ApplicabilityConstraintFormProps
> = ({ value, onChange, error, disabled }) => {
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(value, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!jsonMode) {
      setJsonText(JSON.stringify(value, null, 2));
    }
  }, [value, jsonMode]);

  const currentType = (value?.type as TemplateType) ?? "alwaysTrue";

  const handleTypeChange = (next: TemplateType) => {
    onChange(defaultsFor(next));
  };

  const handleField = <K extends string>(key: K, fieldValue: unknown) => {
    onChange({ ...value, [key]: fieldValue });
  };

  const handleChild = (idx: number, childValue: Constraint) => {
    const list = Array.isArray(value.constraints)
      ? [...(value.constraints as Constraint[])]
      : [];
    list[idx] = childValue;
    onChange({ ...value, constraints: list });
  };

  const addChild = () => {
    const list = Array.isArray(value.constraints)
      ? [...(value.constraints as Constraint[])]
      : [];
    list.push({ type: "alwaysTrue" });
    onChange({ ...value, constraints: list });
  };

  const removeChild = (idx: number) => {
    const list = Array.isArray(value.constraints)
      ? (value.constraints as Constraint[]).filter((_, i) => i !== idx)
      : [];
    onChange({ ...value, constraints: list });
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed);
    } catch {
      setJsonError("Nieprawidłowy JSON");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-neutral-700">
          Warunek stosowalności
        </label>
        <label className="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer">
          <input
            type="checkbox"
            checked={jsonMode}
            onChange={() => setJsonMode((v) => !v)}
            className="h-3 w-3 text-primary-600 border-neutral-300 rounded"
            disabled={disabled}
          />
          Edycja JSON (zaawansowane)
        </label>
      </div>

      {jsonMode ? (
        <div>
          <textarea
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={8}
            disabled={disabled}
            className="w-full text-xs font-mono p-2 border border-neutral-300 rounded"
          />
          {jsonError && (
            <p className="text-xs text-red-600 mt-1">{jsonError}</p>
          )}
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
      ) : (
        <div className="space-y-2 p-3 bg-neutral-50 rounded border border-neutral-200">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Typ
            </label>
            <select
              value={currentType}
              onChange={(e) => handleTypeChange(e.target.value as TemplateType)}
              disabled={disabled}
              className="w-full text-sm border border-neutral-300 rounded p-1.5"
            >
              {TEMPLATE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {(currentType === "equals" ||
            currentType === "dateBefore" ||
            currentType === "dateOnOrAfter") && (
            <>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Pole
                </label>
                <select
                  value={(value.field as string) ?? FIELD_OPTIONS[0]}
                  onChange={(e) => handleField("field", e.target.value)}
                  disabled={disabled}
                  className="w-full text-sm border border-neutral-300 rounded p-1.5"
                >
                  {FIELD_OPTIONS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  {currentType === "equals" ? "Wartość" : "Data (ISO 8601)"}
                </label>
                {currentType === "equals" ? (
                  <input
                    type="text"
                    value={(value.value as string) ?? ""}
                    onChange={(e) => handleField("value", e.target.value)}
                    placeholder="np. self_service"
                    disabled={disabled}
                    className="w-full text-sm border border-neutral-300 rounded p-1.5"
                  />
                ) : (
                  <>
                    <input
                      type="datetime-local"
                      value={toLocalDateTime((value.value as string) ?? "")}
                      onChange={(e) =>
                        handleField(
                          "value",
                          e.target.value
                            ? new Date(e.target.value).toISOString()
                            : "",
                        )
                      }
                      disabled={disabled}
                      className="w-full text-sm border border-neutral-300 rounded p-1.5"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      Czas polski (Europe/Warsaw) — wartość zapisana jako ISO 8601 UTC
                    </p>
                  </>
                )}
              </div>
            </>
          )}

          {(currentType === "or" || currentType === "and") && (
            <div className="space-y-2">
              <p className="text-xs text-neutral-600">
                {currentType === "or"
                  ? "Spełnione gdy ≥1 z poniższych jest spełnione"
                  : "Spełnione gdy WSZYSTKIE poniższe są spełnione"}
              </p>
              <div className="space-y-2">
                {(value.constraints as Constraint[] | undefined)?.map(
                  (child, idx) => (
                    // Transitional key: combines index with discriminator to
                    // give React reconciler a more stable identity than `idx`
                    // alone when items are added/removed mid-list.
                    // TODO(M-batch-E #8): replace with explicit `id` field
                    // once Constraint ADT exposes one.
                    <div
                      key={`${idx}-${(child?.type as string) ?? "unknown"}`}
                      className="border border-neutral-200 rounded p-2 bg-white"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-medium text-neutral-700">
                          Warunek #{idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeChild(idx)}
                          disabled={disabled}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Usuń
                        </button>
                      </div>
                      <ApplicabilityConstraintForm
                        value={child}
                        onChange={(c) => handleChild(idx, c)}
                        disabled={disabled}
                      />
                    </div>
                  ),
                )}
              </div>
              <button
                type="button"
                onClick={addChild}
                disabled={disabled}
                className="text-xs text-primary-600 hover:text-primary-800"
              >
                + Dodaj warunek
              </button>
            </div>
          )}

          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
      )}
    </div>
  );
};
